define('models/Podio.Models.Conversation',['require','underscore','lib/backbone/Backbone.PodioModel','lib/Podio.Routes','lib/Podio.Config','lib/Podio.Utils','lib/Podio.Colors','models/Podio.Models.User','collections/Podio.Collections.Users'],function(require) {

  var _ = require('underscore');
  var PodioModel = require('lib/backbone/Backbone.PodioModel');
  var routes = require('lib/Podio.Routes');
  var podioConfig = require('lib/Podio.Config');
  var PodioUtils = require('lib/Podio.Utils');
  var podioColors = require('lib/Podio.Colors');

  var firstParticipantsCount = 3;

  return PodioModel.extend({
    urlRoot: function() {
      return routes.conversations_path({format: ''});
    },

    paramRoot: 'conversation',

    has_one: function() {
      return {
        'created_by': require('models/Podio.Models.User')
      };
    },

    has_many: function() {
      return {
        'participants': require('collections/Podio.Collections.Users')
      };
    },

    participantsToNames: function(participants) {
      if (_.isArray(participants)) {     /* array of models */
        return participants.map(function(p) { return p.get('name'); });
      } else {                           /* collection */
        return participants.pluck('name');
      }
    },

    _extractNames: function(participants) {
      return this.participantsToNames(participants).join(', ');
    },

    getCurrentUserId: function() {
      return podioConfig.user.user_id;
    },

    getOtherParticipantsNames: function() {
      return this._extractNames(this.getOtherParticipants());
    },

    // Will get other participant for one-on-one conversations
    getOtherParticipant: function() {
      var others = this.getOtherParticipants();

      if (others.length) {
        return _.first(others);
      } else {
        return this.get('participants').first();
      }
    },

    // Will get other participant that is not current user
    getOtherParticipants: function() {
      var currentUserId = this.getCurrentUserId();

      return _.sortBy(this.get('participants').reject(function(p) {
        return p.get('user_id') === currentUserId;
      }), function(p) {
        return p.get('name');
      });
    },

    // All participants including self sorted alphabetically by name
    getAllParticipantsSorted: function() {
      return this.get('participants').sortBy(function(p) {
        return p.get('name');
      });
    },

    _getDefaultCount: function() {
      return firstParticipantsCount;
    },

    _getSplitParticipants: function(options) {
      var otherParticipants = _(this.getOtherParticipants());
      var count = options && options.firstParticipantsCount ? options.firstParticipantsCount : this._getDefaultCount();

      return {'first': otherParticipants.first(count),
              'more': otherParticipants.rest(count)};
    },

    getFirstParticipants: function(options) {
      return this._getSplitParticipants(options)['first'];
    },

    getMoreParticipantsCount: function(options) {
      return this._getSplitParticipants(options)['more'].length;
    },

    getMoreParticipantsTooltip: function(options) {
      var moreParticipants = this._getSplitParticipants(options)['more'];
      var names = this.participantsToNames(moreParticipants);
      names = _.sortBy(names, function(name) { return name.toLowerCase(); });
      var escapedNames = _.map(names, _.escape);

      return JSON.stringify(escapedNames);
    },

    getAllParticipantsTooltip: function() {
      var sortedNames = this.getOtherParticipants().map(function(participant) {
        if (PodioUtils.showFeature('tooltip_remove_escaping')) {   
          return {
            name: participant.get('name'),
            presenceState: participant.get('presence_state_info')
          };
        }
        else {
          return {
            name: _.escape(participant.get('name')),
            presenceState: participant.get('presence_state_info')
          };
        }
      });
      return JSON.stringify(sortedNames);
    },

    getListTooltipData: function() {
      var data = {
        headline: this.getOtherParticipantsNames(),
        subline: this.get('subject') ? this.get('subject') : this.get('excerpt')
      };
      return JSON.stringify(data);
    },

    isExperimentalGroupStyling: PodioUtils.showFeature('group_chat_exp'),

    getGroupChatStyling: function() {
      var createdOn = this.get('created_on');
      var label = this.get('subject') ? this.get('subject') : createdOn;
      var colorFromString = podioColors.getColorFromString(label);
      var lightColor = podioColors.getClosestColor(podioColors.defaults, colorFromString);
      var darkColor = podioColors.defaultCompliments[lightColor];

      // Let's mix things up by using a cross hatch if we've got an even millisecond
      function crossHatch() {
        if (!!(parseInt(createdOn.charAt(createdOn.length - 1)) % 2)) {
          return 'background-image: url(/images/crosshatch-white.png);';
        }
        return '';
      }

      return 'background-color: #' + lightColor + ';'
              + crossHatch()
              + 'border: 1px solid #' + darkColor + ';'
              + 'color: #' + darkColor + ';';
    },

    getGroupChatAbbr: function() {
      var words = [];
      // Get the first three entries, whether they're words from the title
      // or participants in the conversation
      if (this.get('subject')) {
        words = this.get('subject').split(' ').slice(0,3);
      } else {
        words = this.participantsToNames(this.getFirstParticipants());
      }

      // Return the first letter of each entry for use as the conversation label.
      return words.reduce(function(prev, a) { return prev + a.charAt(0); }, '').toUpperCase();
    },

    canLeaveConversation: function() {
      return this.get('participants').length > 2;
    },

    isGroup: function() {
      return this.get('type') === 'group';
    },

    isDirect: function() {
      return this.get('type') === 'direct';
    },

    isOneOnOne: function() {
      return this.get('participants').length === 2;
    },

    leave: function() {
      return this.request(
        'post',
        routes.leave_conversation_path(this.get('id'))
      );
    },

    isStarred: function() {
      return this.get('starred');
    },

    isUnread: function() {
      return this.get('unread');
    },

    markUnread: function() {
      var url = routes.conversations_mark_as_unread_path(this.get('id'));

      this.set('unread', true);
      this.set('unread_count', 1);
      return this.request('delete', url);
    },

    markRead: function() {
      var url = routes.conversations_mark_as_read_path(this.get('id'));

      this.set('unread', false);
      this.set('unread_count', 0);
      return this.request('post', url);
    },

    star: function() {
      var url = routes.conversations_star_path(this.get('id'));

      this.set('starred', true);
      return this.request('post', url);
    },

    unstar: function() {
      var url = routes.conversations_unstar_path(this.get('id'));

      this.set('starred', false);
      return this.request('delete', url);
    },

    lastEventOrCreatedOn: function() {
      return this.get('last_event_on') || this.get('created_on');
    }

  });

});

